import React, { Suspense, useEffect } from 'react';
import { Auth0Context } from '@bloomays-lib/types.shared';
import { setUser as setUserMonitoring } from '@bloomays-lib/common.monitoring/browser';
import { setUser as setUserLogger } from '@bloomays-lib/common.logger/browser';
import { useAuthStore } from '../stores/authStore';

export type AuthProviderProps = {
  children: React.ReactNode;
  auth0Context: Auth0Context;
  loaderAuthComponent: React.ReactNode;
};

const setUser = (user: { displayName: string; email: string }): void => {
  setUserMonitoring(user);
  setUserLogger({
    displayName: user.displayName,
    email: user.email,
    isBloomie: (user.email || '').includes('@bloomays.com'),
  });
};

type AuthDataProps = Omit<AuthProviderProps, 'loaderAuthComponent'> & {
  location: string;
  setUser: typeof setUser;
};

const AuthData = ({ children, auth0Context, location, setUser }: AuthDataProps) => {
  const authFetch = useAuthStore((state) => state.authFetch);
  const authError = useAuthStore((state) => state.authError);
  const auth = useAuthStore((state) => state.auth);

  useEffect(() => {
    if (auth) {
      // si on a un changement de contexte, on recharge le jeton
      if (!auth.isAnonymous && auth.isAuthenticated !== auth0Context.isAuthenticated) {
        authFetch(auth0Context, location);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth0Context, location]);

  if (authError) {
    throw authError;
  }

  if (!auth) {
    throw authFetch(auth0Context, location);
  }

  if (auth?.user) {
    setUser(auth.user);
  }

  return children;
};

export function AuthProvider({ children, auth0Context, loaderAuthComponent }: AuthProviderProps) {
  return (
    <Suspense fallback={loaderAuthComponent}>
      <AuthData auth0Context={auth0Context} location={window.location.href} setUser={setUser}>
        {children}
      </AuthData>
    </Suspense>
  );
}
